import React, { useEffect, useState } from "react";
import { adminApi } from "../../utils/axiosConfig";

const AllAdmins = ({ superAdminEmail }) => {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newPasswords, setNewPasswords] = useState({});
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [expandedAdmin, setExpandedAdmin] = useState(null);

  const fetchAdmins = async () => {
    try {
      const res = await adminApi.get(`/api/admin/all-admins`);
      setAdmins(res.data.admins);
    } catch (err) {
      setError("Erreur lors du chargement des administrateurs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAdmins();
  }, []);

  const handleDelete = async (id, email) => {
    if (!window.confirm(`Supprimer l'administrateur ${email} ?`)) return;

    try {
      await adminApi.delete(`/api/admin/admin/${id}`);
      setMessage(`Administrateur ${email} supprimé`);
      setTimeout(() => setMessage(""), 3000);
      fetchAdmins();
    } catch (err) {
      setError("Erreur lors de la suppression");
      setTimeout(() => setError(""), 3000);
    }
  };

  const handlePasswordChange = (id, value) => {
    setNewPasswords({ ...newPasswords, [id]: value });
  };

  const handleUpdatePassword = async (id, email) => {
    const newPassword = newPasswords[id];
    if (!newPassword || newPassword.length < 6) {
      setError("Le mot de passe doit contenir au moins 6 caractères");
      setTimeout(() => setError(""), 3000);
      return;
    }

    try {
      await adminApi.patch(
        `/api/admin/update-password/${id}`,
        { newPassword }
      );
      setMessage(`Mot de passe mis à jour pour ${email}`);
      setTimeout(() => setMessage(""), 3000);
      setNewPasswords({ ...newPasswords, [id]: "" });
    } catch (err) {
      setError("Erreur lors de la mise à jour du mot de passe");
      setTimeout(() => setError(""), 3000);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Chargement des administrateurs...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-message">
        <span className="error-icon">⚠️</span>
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className="admins-container">
      <style>{styles}</style>

      <div className="admins-header">
        <p>
          {admins.length} administrateur
          {admins.length !== 1 ? "s" : ""} enregistré
          {admins.length !== 1 ? "s" : ""}
        </p>
      </div>

      {message && (
        <div className="notification success">
          <span className="notification-icon">✓</span>
          {message}
        </div>
      )}

      {error && (
        <div className="notification error">
          <span className="notification-icon">⚠️</span>
          {error}
        </div>
      )}

      {admins.length === 0 ? (
        <div className="empty-state">
          <span className="empty-icon">👤</span>
          <p>Aucun administrateur trouvé</p>
        </div>
      ) : (
        <div className="admins-list">
          {admins.map((admin) => (
            <div
              key={admin._id}
              className={`admin-card ${
                expandedAdmin === admin._id ? "expanded" : ""
              }`}
              onClick={() =>
                setExpandedAdmin(expandedAdmin === admin._id ? null : admin._id)
              }
            >
              <div className="admin-info">
                <div className="admin-avatar">
                  {admin.email.charAt(0).toUpperCase()}
                </div>
                <div className="admin-details">
                  <h3>{admin.email}</h3>
                  {admin.email === superAdminEmail && (
                    <span className="admin-badge">Super Admin</span>
                  )}
                </div>
                <div className="admin-actions">
                  <button
                    className="toggle-btn"
                    aria-label={expandedAdmin === admin._id ? "Réduire" : "Développer"}
                    onClick={(e) => {
                      e.stopPropagation();
                      setExpandedAdmin(expandedAdmin === admin._id ? null : admin._id);
                    }}
                  >
                    {expandedAdmin === admin._id ? "▲" : "▼"}
                  </button>
                </div>
              </div>

              {expandedAdmin === admin._id && (
                <div className="admin-expanded">
                  <div className="password-update">
                    <h4>Modifier le mot de passe</h4>
                    <div className="input-group">
                      <input
                        type="password"
                        placeholder="Nouveau mot de passe"
                        value={newPasswords[admin._id] || ""}
                        onChange={(e) => handlePasswordChange(admin._id, e.target.value)}
                        className="password-input"
                        onClick={(e) => e.stopPropagation()} // <-- stop propagation ici
                      />
                      <button
                        className="update-btn"
                        onClick={(e) => {
                          e.stopPropagation(); // <-- stop propagation ici
                          handleUpdatePassword(admin._id, admin.email);
                        }}
                      >
                        Mettre à jour
                      </button>
                    </div>
                  </div>

                  {admin.email !== superAdminEmail && (
                    <div className="delete-section">
                      <button
                        className="delete-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(admin._id, admin.email);
                        }}
                      >
                        Supprimer cet administrateur
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const styles = `
  :root {
    --primary: #4361ee;
    --primary-light: #e0e7ff;
    --primary-dark: #3a56d4;
    --error: #ef233c;
    --error-light: #fde8ea;
    --success: #4cc9f0;
    --success-light: #e8f7fc;
    --text: #2b2d42;
    --text-light: #8d99ae;
    --bg: #f8f9fa;
    --card-bg: #ffffff;
    --border: #e2e8f0;
    --border-radius: 12px;
    --shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
    --transition: all 0.2s ease-in-out;
  }

  .admins-container {
    max-width: 800px;
    margin: 0 auto;
    padding: 1.5rem;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
    color: var(--text);
  }

  .admins-header {
    margin-bottom: 2rem;
    text-align: center;
  }

  .admins-header h2 {
    font-size: 1.75rem;
    font-weight: 700;
    color: var(--primary);
    margin-bottom: 0.5rem;
  }

  .admins-header p {
    color: var(--text-light);
    font-size: 0.9rem;
  }

  .admins-list {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .admin-card {
    background: var(--card-bg);
    border-radius: var(--border-radius);
    box-shadow: var(--shadow);
    overflow: hidden;
    transition: var(--transition);
    border: 1px solid var(--border);
  }

  .admin-card:hover {
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
  }

  .admin-card.expanded {
    border-color: var(--primary);
  }

  .admin-info {
    display: flex;
    align-items: center;
    padding: 1.25rem;
    cursor: pointer;
  }

  .admin-avatar {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background-color: var(--primary-light);
    color: var(--primary);
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: bold;
    margin-right: 1rem;
    flex-shrink: 0;
  }

  .admin-details {
    flex-grow: 1;
  }

  .admin-details h3 {
    font-size: 1rem;
    font-weight: 600;
    margin-bottom: 0.25rem;
  }

  .admin-badge {
    display: inline-block;
    background-color: var(--primary-light);
    color: var(--primary);
    font-size: 0.7rem;
    padding: 0.25rem 0.5rem;
    border-radius: 50px;
    font-weight: 600;
  }

  .admin-actions {
    margin-left: auto;
  }

  .toggle-btn {
    background: none;
    border: none;
    color: var(--text-light);
    font-size: 1rem;
    cursor: pointer;
    padding: 0.5rem;
    border-radius: 50%;
    transition: var(--transition);
  }

  .toggle-btn:hover {
    background-color: var(--primary-light);
    color: var(--primary);
  }

  .admin-expanded {
    padding: 0 1.25rem 1.25rem;
    border-top: 1px solid var(--border);
    margin-top: 0.5rem;
    padding-top: 1.25rem;
  }

  .password-update h4 {
    font-size: 0.9rem;
    font-weight: 600;
    margin-bottom: 0.75rem;
    color: var(--text-light);
  }

  .input-group {
    display: flex;
    gap: 0.5rem;
  }

  .password-input {
    flex-grow: 1;
    padding: 0.75rem 1rem;
    border: 1px solid var(--border);
    border-radius: var(--border-radius);
    font-size: 0.9rem;
    transition: var(--transition);
  }

  .password-input:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: 0 0 0 3px rgba(67, 97, 238, 0.1);
  }

  .update-btn {
    padding: 0.75rem 1.5rem;
    background-color: var(--primary);
    color: white;
    border: none;
    border-radius: var(--border-radius);
    font-weight: 600;
    font-size: 0.85rem;
    cursor: pointer;
    transition: var(--transition);
    white-space: nowrap;
  }

  .update-btn:hover {
    background-color: var(--primary-dark);
    transform: translateY(-1px);
  }

  .delete-section {
    margin-top: 1.5rem;
    padding-top: 1.5rem;
    border-top: 1px dashed var(--border);
  }

  .delete-btn {
    padding: 0.75rem 1.5rem;
    background-color: var(--error-light);
    color: var(--error);
    border: none;
    border-radius: var(--border-radius);
    font-weight: 600;
    font-size: 0.85rem;
    cursor: pointer;
    transition: var(--transition);
    width: 100%;
  }

  .delete-btn:hover {
    background-color: var(--error);
    color: white;
  }

  .notification {
    padding: 1rem;
    border-radius: var(--border-radius);
    margin-bottom: 1.5rem;
    display: flex;
    align-items: center;
    gap: 0.75rem;
    animation: fadeIn 0.3s ease-out;
  }

  .notification.success {
    background-color: var(--success-light);
    color: var(--success);
  }

  .notification.error {
    background-color: var(--error-light);
    color: var(--error);
  }

  .notification-icon {
    font-weight: bold;
  }

  .empty-state {
    text-align: center;
    padding: 3rem 1rem;
    color: var(--text-light);
  }

  .empty-icon {
    font-size: 2rem;
    margin-bottom: 1rem;
    display: inline-block;
  }

  .loading-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 2rem;
  }

  .loading-spinner {
    width: 40px;
    height: 40px;
    border: 4px solid rgba(67, 97, 238, 0.1);
    border-radius: 50%;
    border-top-color: var(--primary);
    animation: spin 1s linear infinite;
    margin-bottom: 1rem;
  }

  .error-message {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 1rem;
    background-color: var(--error-light);
    color: var(--error);
    border-radius: var(--border-radius);
    margin-bottom: 1.5rem;
  }

  .error-icon {
    font-size: 1.2rem;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

export default AllAdmins;
